import React, { useEffect } from 'react';
import { Col, Row, FormGroup, Input, Form, Label, Button } from 'reactstrap';
import { IoClose } from 'react-icons/io5';

const Topbar = ({
  stocks,
  filters,
  setFilters,
  filterStockData,
  setShowOutOfStockMessage,
}) => {
  // Get item from localStorage
  const getCarouselItem = () => {
    try {
      for (let i = 1; i <= 9; i++) {
        const item = localStorage.getItem(`carouselItem_${i}`);
        if (item) {
          const parsedItem = JSON.parse(item);
          if (Date.now() <= parsedItem.expiry) {
            return parsedItem.value.content;
          } else {
            localStorage.removeItem(`carouselItem_${i}`);
          }
        }
      }
      return null;
    } catch (error) {
      console.error('Error retrieving carousel item:', error);
      return null;
    }
  };

  // Set initial filters from localStorage when component mounts
  useEffect(() => {
    const carouselFishType = getCarouselItem();
    const availableFishTypes = uniqueValues('Product_Name');
    const selectedVessel = localStorage.getItem('selectedVessel'); // Get selectedVessel from localStorage

    // Update filters based on localStorage values
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };

      // Set Product_Name from carousel if available and not already set
      if (carouselFishType && !prevFilters.Product_Name) {
        if (availableFishTypes.includes(carouselFishType)) {
          updatedFilters.Product_Name = carouselFishType;
          setShowOutOfStockMessage(false);
        } else {
          updatedFilters.Product_Name = '';
          setShowOutOfStockMessage(true);
        }
      }

      // Set Boat_Number from selectedVessel if available and not already set
      if (selectedVessel && !prevFilters.Boat_Number) {
        updatedFilters.Boat_Number = selectedVessel.toUpperCase();
      }

      return updatedFilters;
    });
  }, [stocks, setFilters, setShowOutOfStockMessage]);

  // Handle filter value changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: name === 'Boat_Number' ? value.toUpperCase() : value,
    }));
    setShowOutOfStockMessage(false);
  };

  // Extract unique values for dropdown options
  const uniqueValues = (field) => {
    return [...new Set(stocks.map((item) => item[field]))];
  };

  // Reset filters and clear selectedVessel from localStorage
  const resetFilters = () => {
    setFilters({
      Product_Name: '',
      Boat_Number: '',
      Quality_Grade: '',
      Harbour: '',
      minPrice: '',
      maxPrice: '',
      minWeight: '',
      maxWeight: '',
    });
    setShowOutOfStockMessage(false);
    localStorage.removeItem('selectedVessel'); // Clear selectedVessel from localStorage
  };

  return (
    <div className="FilterTopBar">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <h5>Filters</h5>
        <button
          onClick={resetFilters}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            background: 'white',
            color: 'gray',
            borderRadius: '5px',
            border: 'none',
            padding: '5px 10px',
            cursor: 'pointer',
          }}
        >
          Clear
          <IoClose
            style={{ borderRadius: '50%', background: 'white', padding: '2px' }}
          />
        </button>
      </div>
      <Form>
        <br />
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for="Product_Name">Fish Type</Label>
              <Input
                style={{ paddingLeft: '10px' }}
                type="select"
                name="Product_Name"
                id="Product_Name"
                value={filters.Product_Name || ''}
                onChange={handleFilterChange}
              >
                <option value="">All Available Fish Types</option>
                {uniqueValues('Product_Name').map((value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="Boat_Number">Boat Number</Label>
              <Input
                type="text"
                name="Boat_Number"
                id="Boat_Number"
                value={filters.Boat_Number || ''}
                onChange={handleFilterChange}
                onInput={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
                placeholder="Enter boat number (e.g., BN123)"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="Quality_Grade">Quality</Label>
              <Input
                type="select"
                name="Quality_Grade"
                id="Quality_Grade"
                value={filters.Quality_Grade || ''}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {uniqueValues('Quality_Grade').map((value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="Harbour">Harbour</Label>
              <Input
                type="select"
                name="Harbour"
                id="Harbour"
                value={filters.Harbour || ''}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {uniqueValues('Harbour').map((value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="minPrice">Min Price (LKR)</Label>
              <Input
                type="number"
                name="minPrice"
                id="minPrice"
                value={filters.minPrice || ''}
                onChange={handleFilterChange}
                placeholder="Min Price"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="maxPrice">Max Price (LKR)</Label>
              <Input
                type="number"
                name="maxPrice"
                id="maxPrice"
                value={filters.maxPrice || ''}
                onChange={handleFilterChange}
                placeholder="Max Price"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="minWeight">Min Weight</Label>
              <Input
                type="number"
                name="minWeight"
                id="minWeight"
                value={filters.minWeight || ''}
                onChange={handleFilterChange}
                placeholder="Min Weight (kg)"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="maxWeight">Max Weight</Label>
              <Input
                type="number"
                name="maxWeight"
                id="maxWeight"
                value={filters.maxWeight || ''}
                onChange={handleFilterChange}
                placeholder="Max Weight (kg)"
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                style={{
                  marginTop: '20px',
                  width: '100%',
                  maxWidth: '200px',
                  backgroundColor: 'black',
                  color: 'white',
                  border: 'none',
                  height: '43px',
                  borderRadius: '20px',
                }}
                onClick={filterStockData}
              >
                Filter
              </Button>
            </FormGroup>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Topbar;